import React, { useState, useEffect } from 'react';
import { 
  History, CheckCircle, XCircle, AlertTriangle, User, FileText, Calendar 
} from 'lucide-react';
import { useToast, ToastContainer } from '../common/Toast';
import PageBreadcrumb from '../common/PageBreadcrumb';
import PageHeader from '../common/PageHeader';
import DataTable from '../common/DataTable';
import PreContractService from '../../services/preContractService';

const ComplianceLogs = () => {
  const { error: showError, toasts, removeToast } = useToast();
  const [loading, setLoading] = useState(true);
  const [logs, setLogs] = useState([]);

  const loadLogs = async () => {
    setLoading(true);
    try {
      const response = await PreContractService.getComplianceLogs();
      setLogs(response.data || []);
    } catch (err) {
      console.error(err);
      showError('Failed to load compliance logs');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadLogs();
  }, []);

  const getStatusBadge = (status) => {
    if (status === 'Pass') return <span className="badge bg-success-subtle text-success border border-success-subtle"><CheckCircle size={14} className="me-1"/> Pass</span>;
    if (status === 'Fail') return <span className="badge bg-danger-subtle text-danger border border-danger-subtle"><XCircle size={14} className="me-1"/> Fail</span>;
    return <span className="badge bg-warning-subtle text-warning border border-warning-subtle"><AlertTriangle size={14} className="me-1"/> Warning</span>;
  };

  const headerActions = [
    { label: '', icon: <History size={18} />, onClick: loadLogs, variant: 'outline' }
  ];

  return (
    <>
      <PageBreadcrumb />
      <PageHeader 
        title="Compliance Check Logs" 
        description="Audit history of all pre-contract validation checks" 
        actions={headerActions} 
      />
      <div className="page-container">
        <DataTable
          showActions={false}
          data={logs}
          columns={[
            {
              key: 'status',
              label: 'Result',
              width: '120px',
              render: (value) => getStatusBadge(value)
            },
            {
              key: 'contract',
              label: 'Contract',
              render: (value) => (
                <div className="d-flex align-items-center gap-2">
                  <FileText size={16} className="text-muted" />
                  <span className="fw-medium">{value?.title || 'Unknown Contract'}</span>
                </div>
              )
            },
            {
              key: 'results',
              label: 'Failure Reason / Details',
              render: (value, row) => {
                if (row.status === 'Pass') return <span className="text-muted small">All checks passed successfully</span>;
                
                const failures = [];
                if (value?.policyCheck?.status !== 'Pass') {
                    failures.push(`${value.policyCheck.failedPolicies.length} Policy Failures`);
                }
                if (value?.doaCheck?.status !== 'Pass') {
                    failures.push(`DoA Rejection`);
                }
                if (value?.dueDiligenceCheck?.status !== 'Pass') {
                    failures.push(`Risk Flag`);
                }
                return <span className="text-danger small fw-bold">{failures.join(', ')}</span>;
              }
            },
            {
              key: 'triggeredBy',
              label: 'Triggered By',
              width: '180px',
              render: (value) => (
                <div className="d-flex align-items-center gap-2 text-secondary">
                   <User size={14} />
                   <span className="small">{value ? `${value.firstName} ${value.lastName}` : 'System'}</span>
                </div>
              )
            },
            {
              key: 'createdAt',
              label: 'Date',
              width: '180px',
              render: (value) => (
                <div className="d-flex align-items-center gap-2 text-muted small">
                   <Calendar size={14} />
                   {new Date(value).toLocaleString()}
                </div>
              )
            }
          ]}
          loading={loading}
          emptyMessage="No compliance logs found."
        />
      </div>
      <ToastContainer toasts={toasts} removeToast={removeToast} />
    </>
  );
};

export default ComplianceLogs;